import React from 'react';
import { Box, Paper, Typography, Skeleton, Divider, Stack, Card } from '@mui/material';
import ReceiptIcon from '@mui/icons-material/Receipt';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import CallMadeIcon from '@mui/icons-material/CallMade';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import LockIcon from '@mui/icons-material/Lock';
import { useTranslation } from 'react-i18next';
import { useFormatter } from '../../../context/FormatterContext';

interface TaxDataItem {
    label: string;
    amount: number;
    translationKey: string;
}

interface OtherTransactionsBoxProps {
    otherTransactions: TaxDataItem[];
    isPaid: boolean;
    isLoading?: boolean;
}

const OtherTransactionsBox: React.FC<OtherTransactionsBoxProps> = ({
    otherTransactions = [],
    isPaid,
    isLoading
}) => {
    const { t } = useTranslation();
    const { formatCurrency } = useFormatter();

    // Default data structure
    const defaultOtherTransactions: TaxDataItem[] = [
        { label: 'Donations received', amount: 0, translationKey: 'taxReports.donationsReceived' },
        { label: 'Donations sent', amount: 0, translationKey: 'taxReports.donationsSent' },
        { label: 'Lost/stolen assets', amount: 0, translationKey: 'taxReports.lostStolen' }
    ];

    const displayOtherTransactions = otherTransactions.length > 0 ? otherTransactions : defaultOtherTransactions;

    // Get icon for each category
    const getCategoryIcon = (index: number) => {
        const icons = [
            <VolunteerActivismIcon sx={{ color: '#4CAF50', fontSize: 20 }} />,
            <CallMadeIcon sx={{ color: '#2196F3', fontSize: 20 }} />,
            <ErrorOutlineIcon sx={{ color: '#FF5722', fontSize: 20 }} />
        ];
        return icons[index] || icons[0];
    };

    const formatAmount = (value: number | null) => {
        if (value === null || value === undefined) return '---';
        return formatCurrency(value);
    };

    return (
        <Card sx={{}}>
            {/* Header */}
            <Box sx={{ mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2 }}>
                    <ReceiptIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                    <Typography variant="h6">
                        {t('taxReports.otherRelevantTransactions', 'Sonstige Transaktionen')}
                    </Typography>
                </Box>
            </Box>

            {/* Data Items */}
            <Box sx={{ p: 2 }}>
                <Stack spacing={2}>
                    {displayOtherTransactions.map((item, index) => (
                        <Box key={index} sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            py: 1,
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                {getCategoryIcon(index)}
                                <Typography variant="body1">
                                    {t(item.translationKey, item.label)}
                                </Typography>
                            </Box>

                            {isLoading ? (
                                <Skeleton width="100px" height="24px" />
                            ) : isPaid ? (
                                <Typography variant="body1" color="text.primary">
                                    {formatAmount(item.amount)}
                                </Typography>
                            ) : (
                                <LockIcon sx={{ 
                                    color: 'text.secondary', 
                                    fontSize: 20 
                                }} />
                            )}
                        </Box>
                    ))}
                </Stack>
            </Box>
        </Card>
    );
};

export default OtherTransactionsBox;